import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import type { PermitApplication } from "@/api/permit_applications";
import { PERMIT_APPLICATION_STATUS_LABELS, PERMIT_TYPE_LABELS } from "@/api/permit_applications";
import { listResidentPermits } from "@/api/residents";
import {
  permitApplicationStatusPillClass,
} from "@/components/permit_suite/permitSuiteDisplay";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Button } from "@/components/ui/button";
import { usePermitSuiteNavigation } from "@/context/PermitSuiteNavigationContext";
import { useUser } from "@/context/UserProvider";
import { cn } from "@/lib/utils";

type ResidentPermitsListProps = {
  municipalityId: number;
  residentId: number;
};

export function ResidentPermitsList({ municipalityId, residentId }: ResidentPermitsListProps) {
  const { currentUser } = useUser();
  const { navigateToPermitApplication } = usePermitSuiteNavigation();
  const permitSuiteEnabled = Boolean(currentUser?.permit_suite);

  const { data: permits = [], isLoading, error } = useQuery({
    queryKey: ["resident_permits", municipalityId, residentId],
    queryFn: () => listResidentPermits(municipalityId, residentId) as Promise<PermitApplication[]>,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading permits…
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">Could not load permits.</p>;
  }

  if (permits.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No linked permit applications yet.
      </p>
    );
  }

  return (
    <ul className="space-y-2" data-testid="resident-permits-list">
      {permits.map((permit) => (
        <li key={permit.id} className="rounded-lg border bg-background p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium">{permit.title}</p>
              {permit.project_address ? (
                <p className="mt-1 truncate text-xs text-muted-foreground">{permit.project_address}</p>
              ) : null}
            </div>
            <span className="shrink-0 text-[10px] text-muted-foreground">#{permit.id}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                permitApplicationStatusPillClass(permit.status),
              )}
            >
              {PERMIT_APPLICATION_STATUS_LABELS[permit.status]}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {PERMIT_TYPE_LABELS[permit.permit_type]}
            </span>
            <span className="text-[11px] text-muted-foreground">
              Updated {formatRelativeTime(permit.updated_at)}
            </span>
          </div>
          {permitSuiteEnabled ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => navigateToPermitApplication(permit.id)}
            >
              View in Permit Suite
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
